<template>
    <div class="v-select"
         v-c:open="isOpen"
         v-disabled="disabled"
         v-c:v-select-sm="small"
         tabindex="0"
         @keyup.up="updateSearchOffset(-1)"
         @keyup.down="updateSearchOffset(1)"
         @keyup.enter="selectOption"
    >
        <div class="current-value">
            <slot v-if="isEmpty" name="empty-value">
                <div class="text-truncate">
                    {{ emptyValue || __('ui/not_selected') }}
                </div>
            </slot>
            <template v-else-if="multiple">
                <div class="text-truncate">
                    <div v-for="(option, index) of selectedOption" :key="index" class="v-select-multiple-value">
                        <slot name="open-option" :option="option">
                            {{ option[labelKey] }}
                        </slot>
                    </div>
                </div>
            </template>
            <slot v-else name="open-option" :option="selectedOption">
                <div class="text-truncate">
                    {{ selectedOption[labelKey] }}
                </div>
            </slot>
            <span class="drop"/>
        </div>

        <popover auto-bind ref="popover" :offset-arrow="10" @toggle="handleToggle">
            <div class="search" v-if="!disableSearch">
                <input v-model.trim="search"
                       ref="search"
                       @keyup.stop.up="updateSearchOffset(-1)"
                       @keyup.stop.down="updateSearchOffset(1)"
                       @keyup.enter="selectOption"
                       :placeholder="__('ui/search_placeholder')"
                >
                <span class="search-icon"/>
            </div>

            <div class="options" ref="options">
                <div v-if="lazy && search.length < 3" class="option">
                    <div v-if="lazyPlaceholder">
                        {{ lazyPlaceholder }}
                    </div>
                    <div v-else>
                        {{ __('ui/lazy_load_placeholder') }}
                    </div>
                </div>
                <div v-else
                     v-for="(option, index) in filteredOptions"
                     :key="index"
                     class="option"
                     v-c:selected="isSelected(option[valueKey])"
                     v-c:highlighted="searchOffset === index"
                     @mouseover="searchOffset = index"
                     @click="selectOption"
                >
                    <slot name="option" :option="option">
                        <flex v-if="multiple">
                            <div class="multiple-icon"></div>
                            <div>{{ option[labelKey] }}</div>
                        </flex>
                        <template v-else>
                            {{ option[labelKey] }}
                        </template>
                    </slot>

                </div>
            </div>

            <div class="no-results" v-if="filteredOptions.length === 0">
                {{ __('ui/no_result') }}
            </div>
        </popover>
    </div>
</template>

<script>
    export default {
        props: {
            value: {require: true},
            options: [String, Array],
            small: {type: Boolean, default: false},
            multiple: {type: Boolean, default: false},
            disableSearch: {type: Boolean, default: false},
            valueKey: {type: String, default: 'key'},
            labelKey: {type: String, default: 'label'},
            searchKey: {type: String, default: null},
            emptyValue: {type: String, default: null},
            disabled: {type: Boolean, default: false},
            lazy: {type: Boolean, default: false},
            lazyPlaceholder: {type: String, default: null},
        },

        data() {
            return {
                search: '',
                hasMatch: false,
                isOpen: false,
                searchOffset: 0,
            };
        },

        computed: {
            computedOptions() {
                let options = this.options;

                if (typeof options === "string") {
                    if (!this.$store.getters.hasOwnProperty(options)) {
                        throw `Unknown vuex getter: ${options}`;
                    }
                    options = this.$store.getters[options];
                }

                if (!Array.isArray(options)) {
                    throw 'Prop options have to be an array or valid vuex getter';
                }

                if (options.length > 0 && ["string", "number"].includes(typeof options[0])) {
                    return options.map(option => ({key: option, label: option}));
                }

                return options;
            },

            filteredOptions() {
                let searchKey = this.searchKey || this.labelKey;

                if (this.search === '') {
                    return this.computedOptions;
                }

                return this.computedOptions.filter(option => {
                    return (option[searchKey]||'').toLowerCase().includes(this.search.toLowerCase());
                });
            },

            selectedOption() {
                let selectedOptions = this.computedOptions.filter(option => this.isSelected(option[this.valueKey]));
                if (this.multiple) {
                    return selectedOptions;
                }
                return selectedOptions.length > 0 ? selectedOptions[0] : null;
            },

            isEmpty() {
                if (this.multiple) {
                    return this.selectedOption.length === 0;
                }
                return this.selectedOption === null;
            },
        },

        methods: {
            selectOption() {
                let option = this.filteredOptions[this.searchOffset];
                let optionValue = option[this.valueKey];

                if (this.multiple) {
                    let newValue = this.value.slice(0);
                    let index = newValue.findIndex(value => (value+'') === (optionValue+''));
                    if (~index) {
                        newValue.splice(index, 1);
                    } else {
                        newValue.push(optionValue);
                    }
                    this.$emit('input', newValue);
                } else {
                    this.$emit('input', optionValue);
                }

                if (!this.multiple) {
                    this.$refs.popover.close();
                }
            },

            async handleToggle(open) {
                this.isOpen = open;
                this.searchOffset = 0;
                await this.$nextTick();
                if (open) {
                    let selectedOption = this.$refs.options.querySelector('.option.selected');
                    if (selectedOption) {
                        this.$refs.options.scrollTop = selectedOption.offsetTop - 80;
                    }
                    if (!this.disableSearch) {
                        this.$refs.search.focus();
                    }
                    this.$refs.popover.$el.style.minWidth = this.$el.getBoundingClientRect().width + 'px';
                } else {
                    this.search = "";
                }
            },

            updateSearchOffset(change) {
                if (change === 0) {
                    this.searchOffset = 0;
                } else if (change > 0) {
                    if (this.searchOffset < (this.filteredOptions.length-1)) {
                        this.searchOffset++;
                    }
                } else {
                    if (this.searchOffset > 0) {
                        this.searchOffset--;
                    }
                }
            },

            isSelected(value) {
                if (this.multiple) {
                    return !!~this.value.findIndex(_value => (_value+'') === (value+''));
                }
                return (value+'') === (this.value+'');
            },
        },

        watch: {
            search() {
                this.searchOffset = 0;
            },

            async searchOffset() {
                /*
                 * if searchOffset changes through keyboard keys and not mouse the option container need to be scrolled
                 * manually to keep the highlighted option always in the visible area.
                 */

                await this.$nextTick();

                let elOptions = this.$el.querySelector('.popover .options');

                if (elOptions) {
                    let elHighlighted = elOptions.querySelector('.option.highlighted');

                    if (elHighlighted) {
                        let rectOptions = elOptions.getBoundingClientRect();
                        let rectHighlighted = elHighlighted.getBoundingClientRect();
                        let offsetTop = elHighlighted.offsetTop - elOptions.scrollTop;
                        let offsetBottom = rectOptions.height - offsetTop - rectHighlighted.height;

                        if (offsetTop < 0) {
                            elOptions.scrollTop = elHighlighted.offsetTop;
                        } else if (offsetBottom < 0) {
                            elOptions.scrollTop = elHighlighted.offsetTop + rectHighlighted.height - rectOptions.height;
                        }
                    }
                }
            },
        },
    };
</script>